import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'

export interface ApiErrorDetails {
  status?: number
  traceId?: string
  errorCode?: string
}

export interface AmglobalHttpClientOptions {
  /** Base URL for the .NET API (e.g. `import.meta.env.VITE_API_BASE_URL`). */
  baseURL: string
  /**
   * Return a JWT access token, or null when the user is not authenticated.
   * Tenant scope is carried in the token (see `CurrentTenantService` on the backend).
   */
  getAccessToken?: () => string | null | Promise<string | null>
  /** Optional callback for observability — keeps Sentry out of this package. */
  onResponseError?: (error: AxiosError) => void
  /** Called when a 401 cannot be recovered (session cleared). */
  onUnauthorized?: () => void
}

/** Extract RFC 9457 ProblemDetails extensions from an Axios error. */
export function getApiErrorDetails(error: unknown): ApiErrorDetails {
  if (!axios.isAxiosError(error)) return {}
  const data = error.response?.data as Record<string, unknown> | undefined
  const details: ApiErrorDetails = {}
  if (error.response?.status !== undefined) details.status = error.response.status
  if (typeof data?.traceId === 'string') details.traceId = data.traceId
  if (typeof data?.errorCode === 'string') details.errorCode = data.errorCode
  return details
}

/**
 * Axios instance with JSON defaults and optional Bearer injection.
 * All feature-level TanStack Query hooks should receive this client (or a thin wrapper),
 * never raw `axios` imports in UI components.
 */
export function createAmglobalHttpClient(
  options: AmglobalHttpClientOptions,
): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseURL.replace(/\/$/, ''),
    headers: {
      'Content-Type': 'application/json',
    },
  })

  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await options.getAccessToken?.()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // Default JSON Content-Type breaks multipart uploads (bulk ZIP, CV, HR documents).
    if (config.data instanceof FormData) {
      const headers = AxiosHeaders.from(config.headers)
      headers.delete('Content-Type')
      config.headers = headers
    }
    return config
  })

  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          options.onUnauthorized?.()
        }
        options.onResponseError?.(error)
      }
      return Promise.reject(error)
    },
  )

  return client
}

/** @deprecated Use `createAmglobalHttpClient` — alias for cross-project prompt compatibility. */
export const createHttpClient = createAmglobalHttpClient
